/**
    * @description      : Invoice page for hosts to manage invoices and payments
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import { useState, useEffect } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faDownload,
  faEye,
  faFilter,
  faSearch
} from "@fortawesome/free-solid-svg-icons";
import { useGet } from "../../Api";
import CustomTable from "../../Components/CustomTable";
import CustomButton from "../../Components/CustomButton";
import "./style.css";

export const Invoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const { ApiData: InvoicesData, loading: InvoicesLoading, error: InvoicesError, get: GetInvoices } = useGet(`/invoices`);

  useEffect(() => {
    document.title = 'kvr User | Invoice';
    GetInvoices();
  }, []);

  useEffect(() => {
    if (InvoicesData) {
      setInvoices(InvoicesData?.data || []);
      setFilteredInvoices(InvoicesData?.data || []);
    }
  }, [InvoicesData]);

  useEffect(() => {
    // Filter invoices based on search term and status
    let filtered = invoices;
    
    if (searchTerm) {
      filtered = filtered.filter(invoice => 
        invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }
    
    setFilteredInvoices(filtered);
  }, [invoices, searchTerm, statusFilter]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'paid': 'badge-success',
      'pending': 'badge-warning',
      'overdue': 'badge-danger',
      'cancelled': 'badge-secondary'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleDownloadInvoice = (invoiceId) => {
    // Implement download functionality
    console.log('Downloading invoice:', invoiceId);
  };

  const handleViewInvoice = (invoiceId) => {
    // Implement view functionality
    console.log('Viewing invoice:', invoiceId);
  };

  const tableHeaders = [
    { key: 'invoice_number', title: 'Invoice #' },
    { key: 'customer_name', title: 'Customer' },
    { key: 'amount', title: 'Amount' },
    { key: 'due_date', title: 'Due Date' },
    { key: 'status', title: 'Status' },
    { key: 'actions', title: 'Actions' }
  ];

  return (
    <>
      <DashboardLayout>
        <div className="invoice-container">
          <div className="row">
            <div className="col-12">
              <h2 className="page-title">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                Invoices
              </h2>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-md-3">
              <CustomButton
                text="Export"
                variant="primary"
                className="w-100"
                onClick={() => console.log('Export invoices')}
              />
            </div>
          </div>

          {/* Invoices Table */}
          <div className="row">
            <div className="col-12">
              {InvoicesLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="invoice-table-container">
                  <CustomTable headers={tableHeaders}>
                    <tbody>
                      {filteredInvoices.length > 0 ? (
                        filteredInvoices.map((invoice, index) => (
                          <tr key={index} className="invoice-row">
                            <td>
                              <strong>{invoice.invoice_number}</strong>
                            </td>
                            <td>{invoice.customer_name}</td>
                            <td>
                              <strong>{formatCurrency(invoice.amount)}</strong>
                            </td>
                            <td>{formatDate(invoice.due_date)}</td>
                            <td>{getStatusBadge(invoice.status)}</td>
                            <td>
                              <div className="action-buttons">
                                <CustomButton
                                  text="View"
                                  variant="outline-primary"
                                  size="sm"
                                  icon={faEye}
                                  onClick={() => handleViewInvoice(invoice.id)}
                                  className="me-2"
                                />
                                <CustomButton
                                  text="Download"
                                  variant="outline-success"
                                  size="sm"
                                  icon={faDownload}
                                  onClick={() => handleDownloadInvoice(invoice.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <p className="text-muted">No invoices found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};



